import React from 'react';
import { Typography } from 'antd';
import {
  PRIMITIVE_AUTHORITY_FLAGS,
  type PrimitivePresentationState,
} from './primitiveSurfaceState';
import { PrimitiveSurfaceFrame } from './PrimitiveSurfaceFrame';
import styles from './primitives.module.css';

const { Text } = Typography;

/** Server/page-declared next-step guidance — answers PAGE_GRAMMAR whatShouldIDoNext. */
export interface NextStepEntryV0 {
  readonly id: string;
  readonly label: string;
  readonly detail?: string | null;
  /** Optional cross-reference to a PrimaryActionArea/SecondaryActionGroup actionKey — informational only. */
  readonly relatedActionKey?: string | null;
}

export interface NextStepPanelProps {
  readonly presentationState: PrimitivePresentationState;
  readonly steps?: readonly NextStepEntryV0[];
  readonly grantsAuthority?: false;
  readonly grantsApproval?: false;
  readonly 'data-testid'?: string;
}

/**
 * NextStepPanel (Family B) — renders an ordered "what should I do next" list.
 * Purely explanatory: it never itself emits an action request or claims an
 * action is available (that remains PrimaryActionArea's contract).
 */
export function NextStepPanel(props: NextStepPanelProps): React.ReactElement {
  const grantsAuthority = props.grantsAuthority ?? PRIMITIVE_AUTHORITY_FLAGS.grantsAuthority;
  const grantsApproval = props.grantsApproval ?? PRIMITIVE_AUTHORITY_FLAGS.grantsApproval;
  if (grantsAuthority !== false || grantsApproval !== false) {
    throw new Error('NextStepPanel must keep grantsAuthority/grantsApproval=false');
  }

  const steps = props.steps ?? [];

  return (
    <PrimitiveSurfaceFrame
      title="다음 단계"
      presentationState={props.presentationState}
      emptyMessage="현재 안내할 다음 단계가 없습니다."
      data-testid={props['data-testid'] ?? 'next-step-panel'}
      aria-label="다음 단계"
    >
      {steps.length === 0 ? (
        <Text type="secondary">다음 단계 안내가 없습니다.</Text>
      ) : (
        <ol className={styles.primitiveStack} data-testid="next-step-panel-list">
          {steps.map((step) => (
            <li key={step.id} className={styles.primitiveFieldRow}>
              <span className={styles.primitiveFieldLabel}>{step.label}</span>
              {step.detail ? <Text type="secondary">{step.detail}</Text> : null}
            </li>
          ))}
        </ol>
      )}
    </PrimitiveSurfaceFrame>
  );
}
